'use client'

import {
  CardSlider,
  CardSliderHeader,
  CardSliderList,
  CardSliderListItem,
  CardSliderSeeAllButton,
  CardSliderTitle,
} from '@7f-ui/components/card-slider/card-slider'
import { useClientMediaState } from '@7f-ui/hooks/use-media-state'
import type { NexperienceFragment } from '@data-access/lib/gql/experience/documents'
import {
  type NexperienceSearchItemFragment,
  NexperienceSuggestionsDocument,
} from '@data-access/lib/gql/experience/documents'
import { useGraphQL } from '@data-access/lib/graphql-query'
import { ExperienceCard } from '@feat/feat-experience/cards'
import { useExperienceDestinationURL } from '@feat/feat-experience/data'
import { trackClickCta } from '@feat/feat-tracking/cta'
import { useUrlState } from '@feat/feat-url-state/use-url-state'
import { formatIsoDate } from '@utils/utils-format'

import { shopTestId } from '@utils/utils-shop-test-ids'

export type ExperienceSuggestionTeaser = NexperienceSearchItemFragment

export interface ExperienceSuggestionsProps {
  experience: NexperienceFragment
}

export function ExperienceSuggestions({ experience }: ExperienceSuggestionsProps) {
  const isSm = useClientMediaState('sm')
  const isMd = useClientMediaState('md')
  const locationURL = useExperienceDestinationURL(experience.destination?.slug)
  const { urlState } = useUrlState()

  const { data, isLoading } = useGraphQL(
    NexperienceSuggestionsDocument,
    {
      publicId: experience.publicId,
      filter: {
        from: urlState.from && formatIsoDate(urlState.from),
        to: urlState.to && formatIsoDate(urlState.to),
      },
    },
    {
      select: ({ nexperienceSuggestions }) => nexperienceSuggestions,
    }
  )

  if (isLoading || data?.experiences.length === 0) return null

  const buttonProps =
    data?.hasMore && experience.location
      ? {
          children: 'Alle anzeigen',
          href: locationURL,
        }
      : undefined

  return (
    <div className="bleed-container py-8">
      <CardSlider fullBleed={!isMd}>
        <div className="px-6 md:px-0">
          <CardSliderHeader>
            <CardSliderTitle>Mehr Erlebnisse in der Region? Gibt es hier:</CardSliderTitle>
            {isSm && buttonProps && (
              <CardSliderSeeAllButton
                {...buttonProps}
                data-testid={shopTestId('SuggestionList.view-all.desktop')}
              />
            )}
          </CardSliderHeader>
        </div>

        <CardSliderList
          id={experience.externalId}
          isSlidable={!isSm}
          className="px-6 sm:grid-cols-3 md:px-0"
          data-testid={shopTestId('SuggestionList.gallery')}
        >
          {data?.experiences.map(suggestion => (
            <CardSliderListItem key={suggestion.id}>
              <ExperienceCard
                experience={suggestion}
                isFixedWidth={!isSm}
                showSubtitle={false}
                disableSlider={!isSm}
              />
            </CardSliderListItem>
          ))}
        </CardSliderList>
        {!isSm && buttonProps && (
          <div className="grid px-6">
            <CardSliderSeeAllButton
              {...buttonProps}
              onClick={() => trackClickCta('show all', { type: 'experience' })}
              data-testid={shopTestId('SuggestionList.view-all.mobile')}
            />
          </div>
        )}
      </CardSlider>
    </div>
  )
}
